class CrossSellColorSelector extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('[data-color-selector]');
    this.card = this.closest('[data-cross-sell-card]');
    this.variantsData = null;
    this.customButton = this.querySelector('[data-custom-select-button]');
    this.customList = this.querySelector('[data-custom-select-list]');
    this.customText = this.querySelector('[data-custom-select-text]');
    this.colorBullet = this.querySelector('[data-color-bullet]');
    
    if (!this.select || !this.card) return;
    
    this.init();
  }

  init() {
    // Récupérer les données des variantes depuis le JSON
    const variantsScript = this.card.querySelector('[data-product-variants]');
    if (variantsScript) {
      try {
        this.variantsData = JSON.parse(variantsScript.textContent);
      } catch (e) {
        console.error('Error parsing variants data:', e);
        return;
      }
    }

    // Initialiser le custom select
    this.initCustomSelect();

    // Écouter les changements du sélecteur natif (pour compatibilité)
    this.select.addEventListener('change', this.handleColorChange.bind(this));
  }

  initCustomSelect() {
    if (!this.customButton || !this.customList) return;

    // Stocker la référence au conteneur original pour pouvoir le remettre plus tard
    this.originalListParent = this.customList.parentElement;

    // Initialiser l'affichage avec l'option sélectionnée
    this.updateCustomSelectDisplay();

    // Gérer le clic sur le bouton
    this.customButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleCustomSelect();
    });

    // Gérer les clics sur les options
    const options = this.customList.querySelectorAll('[data-option-value]');
    options.forEach(option => {
      option.addEventListener('click', (e) => {
        e.stopPropagation();
        const value = option.getAttribute('data-option-value');
        this.selectOption(value);
      });

      // Navigation clavier
      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const value = option.getAttribute('data-option-value');
          this.selectOption(value);
        }
      });
    });

    // Fermer le select si on clique en dehors
    this.outsideClickHandler = (e) => {
      if (!this.contains(e.target) && !this.customList.contains(e.target)) {
        this.closeCustomSelect();
      }
    };
    document.addEventListener('click', this.outsideClickHandler);

    // Repositionner le dropdown lors du scroll ou resize
    this.scrollHandler = () => {
      if (this.customButton.getAttribute('aria-expanded') === 'true') {
        this.positionDropdown();
      }
    };
    window.addEventListener('scroll', this.scrollHandler, true);
    window.addEventListener('resize', this.scrollHandler);

    // Navigation clavier sur le bouton
    this.customButton.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        this.openCustomSelect();
        // Focus sur la première option
        const firstOption = this.customList.querySelector('[data-option-value]');
        if (firstOption) firstOption.focus();
      }
    });
  }

  toggleCustomSelect() {
    const isOpen = this.customButton.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      this.closeCustomSelect();
    } else {
      this.openCustomSelect();
    }
  }

  openCustomSelect() {
    this.customButton.setAttribute('aria-expanded', 'true');
    
    // Déplacer le dropdown dans le body pour éviter les problèmes d'overflow
    if (!this.customList.parentElement || this.customList.parentElement !== document.body) {
      document.body.appendChild(this.customList);
    }
    
    // S'assurer que le dropdown est visible
    this.customList.removeAttribute('hidden');
    this.customList.style.display = 'block';
    this.customList.style.visibility = 'visible';
    this.customList.style.opacity = '1';
    
    // Calculer la position du dropdown pour éviter qu'il soit coupé
    // Utiliser setTimeout pour s'assurer que le DOM est mis à jour
    setTimeout(() => {
      this.positionDropdown();
    }, 0);
  }

  positionDropdown() {
    if (!this.customButton || !this.customList) return;

    // Attendre le prochain frame pour que le DOM soit mis à jour
    requestAnimationFrame(() => {
      const buttonRect = this.customButton.getBoundingClientRect();
      const list = this.customList;
      
      // Positionner le dropdown sous le bouton
      let top = buttonRect.bottom + 0.5;
      let left = buttonRect.left;
      const width = buttonRect.width;
      
      // Vérifier si le dropdown dépasse en bas de l'écran
      const listHeight = list.offsetHeight || 200;
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      
      if (spaceBelow < listHeight && buttonRect.top > listHeight) {
        // Afficher au-dessus du bouton si pas assez de place en bas
        top = buttonRect.top - listHeight - 0.5;
      }
      
      // Vérifier si le dropdown dépasse à droite
      const spaceRight = window.innerWidth - buttonRect.left;
      if (spaceRight < width) {
        left = window.innerWidth - width - 10;
      }
      
      // Vérifier si le dropdown dépasse à gauche
      if (left < 10) {
        left = 10;
      }
      
      list.style.top = `${top}px`;
      list.style.left = `${left}px`;
      list.style.width = `${width}px`;
      list.style.position = 'fixed';
      list.style.zIndex = '9999';
    });
  }

  closeCustomSelect() {
    this.customButton.setAttribute('aria-expanded', 'false');
    this.customList.setAttribute('hidden', '');
    this.customList.style.display = 'none';
  }


  selectOption(value) {
    // Mettre à jour le select natif
    this.select.value = value;
    
    // Déclencher l'événement change
    const event = new Event('change', { bubbles: true });
    this.select.dispatchEvent(event);

    // Fermer le custom select
    this.closeCustomSelect();
  }

  updateCustomSelectDisplay() {
    if (!this.select || !this.customText || !this.colorBullet) return;

    const selectedOption = this.select.options[this.select.selectedIndex];
    if (!selectedOption) return;

    const color = selectedOption.getAttribute('data-color');
    const text = selectedOption.textContent.trim();

    // Mettre à jour le texte
    this.customText.textContent = text;

    // Mettre à jour le bullet
    if (color) {
      this.colorBullet.style.backgroundColor = color;
      if (color === '#ffffff' || color === '#FFFFFF' || color === 'white') {
        this.colorBullet.style.border = '2px solid rgba(0,0,0,0.1)';
      } else {
        this.colorBullet.style.border = '1px solid rgba(0,0,0,0.1)';
      }
    } else {
      this.colorBullet.style.backgroundColor = 'transparent';
    }

    // Mettre à jour l'état sélectionné dans la liste
    const options = this.customList.querySelectorAll('[data-option-value]');
    options.forEach(opt => {
      if (opt.getAttribute('data-option-value') === this.select.value) {
        opt.classList.add('slider-cross-sells__custom-select-option--selected');
        opt.setAttribute('aria-selected', 'true');
      } else {
        opt.classList.remove('slider-cross-sells__custom-select-option--selected');
        opt.setAttribute('aria-selected', 'false');
      }
    });
  }

  async handleColorChange(event) {
    const selectedProductId = parseInt(event.target.value);
    const selectedVariant = this.variantsData.find(v => v.id === selectedProductId);
    
    if (!selectedVariant) return;

    // Mettre à jour l'image
    const imageElement = this.card.querySelector('[data-product-image]');
    if (imageElement && selectedVariant.image) {
      imageElement.src = selectedVariant.image;
      imageElement.srcset = selectedVariant.image;
    }

    // Mettre à jour le titre
    const titleElement = this.card.querySelector('[data-product-title]');
    if (titleElement) {
      titleElement.textContent = selectedVariant.short_title || selectedVariant.title;
    }

    // Mettre à jour les liens
    const linkElements = this.card.querySelectorAll('[data-product-link]');
    linkElements.forEach(link => {
      link.href = selectedVariant.url;
    });

    // Mettre à jour le prix
    this.updatePrice(selectedVariant);

    // Mettre à jour le badge de réduction
    this.updateBadge(selectedVariant);

    // Mettre à jour le formulaire d'achat (attendre la mise à jour de la variante)
    await this.updateProductForm(selectedVariant);

    // Mettre à jour l'affichage du custom select
    this.updateCustomSelectDisplay();
  }

  updatePrice(variant) {
    // Réinitialiser this.card si nécessaire
    if (!this.card) {
      this.card = this.closest('[data-cross-sell-card]');
    }
    
    const priceContainer = this.card?.querySelector('[data-product-price]');
    if (!priceContainer) return;

    // Utiliser l'API Shopify pour formater le prix
    const priceFormatted = this.formatMoney(variant.price);
    const compareAtPriceFormatted = variant.compare_at_price > variant.price 
      ? this.formatMoney(variant.compare_at_price) 
      : null;

    // Mettre à jour les classes et le contenu
    const priceElement = priceContainer.querySelector('.price');
    if (!priceElement) return;

    // Mettre à jour les classes
    if (compareAtPriceFormatted) {
      priceElement.classList.add('price--on-sale');
    } else {
      priceElement.classList.remove('price--on-sale');
    }

    // Mettre à jour tous les éléments price-item
    const allPriceItems = priceElement.querySelectorAll('.price-item');
    
    // D'abord, mettre à jour les prix de vente (price__sale)
    const saleSection = priceElement.querySelector('.price__sale');
    if (saleSection) {
      // Prix barré dans price__sale (dans un <s>)
      const strikethroughPrice = saleSection.querySelector('s.price-item--regular, s.price-item');
      if (strikethroughPrice && compareAtPriceFormatted) {
        strikethroughPrice.textContent = compareAtPriceFormatted;
      }
      
      // Prix de vente dans price__sale
      const salePrice = saleSection.querySelector('.price-item--sale, .price-item--last:not(s .price-item)');
      if (salePrice) {
        salePrice.textContent = priceFormatted;
      }
    }
    
    // Ensuite, mettre à jour les prix réguliers (price__regular)
    const regularSection = priceElement.querySelector('.price__regular');
    if (regularSection) {
      const regularPrice = regularSection.querySelector('.price-item--regular, .price-item');
      if (regularPrice) {
        const newPrice = compareAtPriceFormatted || priceFormatted;
        regularPrice.textContent = newPrice;
      }
    }
    
    // Fallback: mettre à jour tous les price-item qui n'ont pas été traités
    allPriceItems.forEach((el) => {
      const isInSale = el.closest('.price__sale');
      const isInRegular = el.closest('.price__regular');
      const isStrikethrough = el.tagName === 'S' || el.closest('s');
      const isSalePrice = el.classList.contains('price-item--sale');
      const isRegularPrice = el.classList.contains('price-item--regular');
      
      // Si l'élément a déjà été traité, on passe
      if (isInSale && isStrikethrough && compareAtPriceFormatted) {
        return; // Déjà traité
      }
      if (isInSale && isSalePrice) {
        return; // Déjà traité
      }
      if (isInRegular && isRegularPrice && !compareAtPriceFormatted) {
        return; // Déjà traité
      }
      
      // Sinon, on met à jour selon le contexte
      const oldText = el.textContent.trim();
      let newText = null;
      
      if (isStrikethrough && compareAtPriceFormatted) {
        newText = compareAtPriceFormatted;
      } else if (isSalePrice || (isInSale && !isStrikethrough)) {
        newText = priceFormatted;
      } else if (isRegularPrice || isInRegular) {
        newText = compareAtPriceFormatted || priceFormatted;
      } else {
        newText = priceFormatted;
      }
      
      if (newText && oldText !== newText) {
        el.textContent = newText;
      }
    });

    // Gérer l'affichage des sections
    if (saleSection) {
      if (compareAtPriceFormatted) {
        saleSection.style.display = '';
        saleSection.hidden = false;
        if (regularSection) {
          regularSection.style.display = 'none';
          regularSection.hidden = true;
        }
      } else {
        saleSection.style.display = 'none';
        saleSection.hidden = true;
        if (regularSection) {
          regularSection.style.display = '';
          regularSection.hidden = false;
        }
      }
    } else if (regularSection) {
      regularSection.style.display = '';
      regularSection.hidden = false;
    }
    
    // Forcer un reflow pour s'assurer que le navigateur met à jour l'affichage
    priceElement.offsetHeight;
  }

  updateBadge(variant) {
    const badgeElement = this.card.querySelector('[data-product-badge]');
    if (!badgeElement) return;

    if (variant.compare_at_price > variant.price) {
      const percentageDiscount = Math.round(
        ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
      );
      const discountElement = badgeElement.querySelector('[data-discount-percentage]');
      if (discountElement) {
        discountElement.textContent = percentageDiscount;
      }
      badgeElement.classList.remove('hidden');
    } else {
      badgeElement.classList.add('hidden');
    }
  }

  async updateProductForm(variant) {
    const formContainer = this.card.querySelector('[data-product-form]');
    if (!formContainer) return;

    // Mettre à jour l'ID du produit dans le formulaire
    const productForm = formContainer.querySelector('form');
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        // Récupérer l'ID de la variante par défaut du nouveau produit
        try {
          // Extraire le handle du produit depuis l'URL
          const productUrl = variant.url;
          // Gérer les URLs absolues et relatives, avec ou sans préfixe de langue
          let productHandle = productUrl;
          
          // Si c'est une URL complète, extraire le chemin
          if (productUrl.startsWith('http')) {
            const urlObj = new URL(productUrl);
            productHandle = urlObj.pathname;
          }
          
          // Extraire le handle (partie après /products/ et avant ? ou #)
          const productsMatch = productHandle.match(/\/products\/([^/?#]+)/);
          if (productsMatch) {
            productHandle = productsMatch[1];
          } else {
            // Fallback: prendre la dernière partie de l'URL
            const urlParts = productHandle.split('/');
            productHandle = urlParts[urlParts.length - 1].split('?')[0].split('#')[0];
          }
          
          // Récupérer les informations du produit depuis l'API Shopify
          const response = await fetch(`/products/${productHandle}.js`);
          if (response.ok) {
            const productData = await response.json();
            // Utiliser la première variante disponible ou la variante sélectionnée
            const selectedVariant = productData.variants.find(v => v.available) || productData.variants[0];
            if (selectedVariant) {
              variantInput.value = selectedVariant.id;
              // Déclencher un événement change pour notifier les autres composants
              const changeEvent = new Event('change', { bubbles: true });
              variantInput.dispatchEvent(changeEvent);
            }
          }
        } catch (error) {
          console.error('Error fetching product variant:', error);
          // Fallback: utiliser l'ID du produit (ne fonctionnera que si le produit n'a qu'une seule variante)
          // Dans ce cas, on laisse le formulaire gérer avec la variante par défaut
        }
      }
    }

    // Mettre à jour les custom elements product-form
    const productFormElements = formContainer.querySelectorAll('product-form');
    productFormElements.forEach(element => {
      if (element.dataset.productId) {
        element.dataset.productId = variant.id;
      }
    });

    // Mettre à jour les autres éléments avec data-product-id
    const buyButtons = formContainer.querySelectorAll('[data-product-id]');
    buyButtons.forEach(button => {
      button.dataset.productId = variant.id;
    });
  }

  formatMoney(cents) {
    // Utiliser la fonction de formatage Shopify si disponible
    if (window.Shopify && window.Shopify.formatMoney) {
      const moneyFormat = window.money_format || window.Shopify.money_format || '{{amount}}';
      return window.Shopify.formatMoney(cents, moneyFormat);
    }
    
    // Fallback: utiliser le format depuis le DOM si disponible
    const existingPrice = this.card.querySelector('.price-item--regular');
    if (existingPrice) {
      const existingText = existingPrice.textContent.trim();
      const existingAmount = parseFloat(existingText.replace(/[^\d,.-]/g, '').replace(',', '.'));
      if (!isNaN(existingAmount)) {
        const newAmount = (cents / 100).toFixed(2);
        // Préserver le format existant
        return existingText.replace(existingAmount.toFixed(2).replace('.', ','), newAmount.replace('.', ','));
      }
    }
    
    // Fallback simple
    const amount = (cents / 100).toFixed(2);
    return `${amount.replace('.', ',')} €`;
  }

  getTranslation(key) {
    // Utiliser les traductions Shopify si disponibles
    if (window.Shopify && window.Shopify.translations) {
      return window.Shopify.translations[key] || key;
    }
    return key;
  }
}

// Nettoyer les event listeners lors de la suppression de l'élément
CrossSellColorSelector.prototype.disconnectedCallback = function() {
  if (this.outsideClickHandler) {
    document.removeEventListener('click', this.outsideClickHandler);
  }
  if (this.scrollHandler) {
    window.removeEventListener('scroll', this.scrollHandler, true);
    window.removeEventListener('resize', this.scrollHandler);
  }
  // Remettre le dropdown dans son conteneur d'origine si nécessaire
  if (this.customList && this.customList.parentElement === document.body && this.originalListParent) {
    this.originalListParent.appendChild(this.customList);
  }
};

customElements.define('cross-sell-color-selector', CrossSellColorSelector);

// S'abonner une seule fois à l'événement cartUpdate pour fermer la modale
let cartUpdateSubscribed = false;

function initCartUpdateListener() {
  if (cartUpdateSubscribed) return;
  
  if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
    cartUpdateSubscribed = true;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (response) => {
      // Fermer la modale de cross-sell si elle existe
      const modal = document.querySelector('.product-popup-modal_cart-cross-sell');
      if (modal) {
        const modalDialog = modal.closest('modal-dialog');
        if (modalDialog && typeof modalDialog.hide === 'function') {
          modalDialog.hide();
        } else if (modal.hasAttribute('open')) {
          modal.removeAttribute('open');
        }
      }
    });
  }
}

// Initialiser pour tous les sélecteurs existants
document.addEventListener('DOMContentLoaded', () => {
  // Les custom elements sont déjà définis dans le HTML, donc pas besoin d'initialisation supplémentaire
  // Mais on peut s'assurer que tous les sélecteurs sont bien initialisés
  document.querySelectorAll('cross-sell-color-selector').forEach(element => {
    // L'élément sera automatiquement initialisé par le constructeur
  });
  
  // Initialiser l'écouteur global pour cartUpdate
  initCartUpdateListener();
});

// Initialiser aussi si le script est chargé après DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartUpdateListener);
} else {
  initCartUpdateListener();
}

